import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowUpRight, ArrowDown, ChevronDown, Eye, Globe2, Sparkles, ShieldCheck,
  TrendingUp, Award, Inbox, Plus, Minus, Megaphone, Check, Star, Bookmark, FileBarChart, Loader2,
} from 'lucide-react';
import { FEATURED_TIERS, PRODUCT_CATEGORIES, COUNTRIES } from '../data/mockData';
import { useSubmitSupplierApplication } from '../data/hooks';
import { track, EVENTS } from '../lib/analytics';
import { VerificationBadgeFull } from '../components/VerificationBadge';
import SEO from '../components/SEO';
import Img from '../components/Img';

const HERO_BG = 'https://static.prod-images.emergentagent.com/jobs/78d6f77c-7a7f-48ae-b71b-ec8e8809cfb7/images/4ad6059937baee7d67f0535497c968eccfe7fc143d9d31462a48e40d95fb3cda.png';

const BENEFITS = [
  { icon: Eye, title: 'Increased Visibility', body: 'Indexed across the Giwahs directory, intelligence brief, AI image-search results, and editorial trend pages.' },
  { icon: ShieldCheck, title: 'Buyer Trust', body: 'Four-tier verification system signals legitimacy to operators evaluating you against agents and trading companies.' },
  { icon: Award, title: 'Factory Verification', body: 'Business Verified, Video Verified, Factory Audited, and Premium Trusted badges — built on documentation, not paid placement.' },
  { icon: Globe2, title: 'Global Exposure', body: 'Reach buyers in EU, UK, USA, Japan, Australia, and the Middle East from a single profile.' },
  { icon: Star, title: 'Premium Positioning', body: 'Editorial intelligence-brief presentation — your factory rendered like a Bloomberg feature, not a tile in a marketplace.' },
  { icon: Sparkles, title: 'AI-Assisted Discovery', body: 'Surface in image-based sourcing results when buyers upload product photos — a discovery surface marketplaces don\'t offer.' },
  { icon: Inbox, title: 'RFQ Opportunities', body: 'Receive structured quote requests with material, MOQ, branding, packaging, and timeline already filled in.' },
  { icon: TrendingUp, title: 'Trend-aligned Discovery', body: 'Editorial trend pages (AW26 / SS27) route relevant buyers directly to factories with matching specialties.' },
];

const TIERS = [
  {
    key: 'spotlight',
    label: 'Spotlight',
    price: 1200,
    badge: 'Highest visibility',
    accent: 'bg-[#0A0A0A] text-white',
    impressions: '~28,000 / mo',
    rfqs: '40–80 / mo',
    saves: '180–320 / mo',
    placements: [
      'Top of Factory Directory (priority slot)',
      'Category landing pages (all relevant categories)',
      'Intelligence brief sidebar placement',
      'AI Image Search "Likely Factories" rotation',
      'Trend page editorial feature',
    ],
    notes: 'Best for established factories pursuing brand-tier buyers across multiple categories.',
  },
  {
    key: 'boost',
    label: 'Boost',
    price: 480,
    badge: 'Most popular',
    accent: 'bg-white text-black border-2 border-black',
    impressions: '~10,500 / mo',
    rfqs: '12–25 / mo',
    saves: '60–120 / mo',
    placements: [
      'Factory Directory Featured strip',
      'Single primary category page',
      'Newsletter mention (monthly)',
    ],
    notes: 'Strong mid-tier placement for factories building global awareness.',
  },
  {
    key: 'standard',
    label: 'Standard',
    price: 180,
    badge: 'Entry tier',
    accent: 'bg-white text-black border border-black',
    impressions: '~3,200 / mo',
    rfqs: '4–9 / mo',
    saves: '20–45 / mo',
    placements: [
      'Featured strip rotation (limited slots)',
      'Listed with active Standard tier rotation',
    ],
    notes: 'Entry-level promotional placement — ideal for boutique workshops.',
  },
];

const METRICS = [
  { value: '1.4M', label: 'Monthly Impressions', sub: 'Across directory + intelligence surfaces' },
  { value: '8,200+', label: 'RFQ Requests Sent', sub: 'Submitted via guided RFQ builder' },
  { value: '52,000', label: 'Buyer Saves', sub: 'Bookmarked supplier profiles' },
  { value: '12', label: 'Sourcing Countries', sub: 'Active supplier coverage' },
];

const FAQS = [
  {
    q: 'How does verification work?',
    a: 'Verification is a four-tier system. Business Verified requires registration documentation reviewed by our team. Video Verified requires a facility tour video. Factory Audited requires a third-party audit report (SMETA, BSCI, WRAP, etc.). Premium Trusted Supplier requires all three plus a track record of buyer satisfaction signals. Verification is entirely separate from Featured placement — paying for placement does not unlock badges.',
  },
  {
    q: 'How do Featured placements work?',
    a: 'Featured placements are paid promotional slots that appear in clearly labelled "Sponsored Placement" strips at the top of relevant discovery surfaces. Featured status does not affect your trust score, verification badges, or position in the regular trust-sorted grid. The Featured strip is purely additive promotional space.',
  },
  {
    q: 'Can factories apply from any country?',
    a: 'Yes — Giwahs is global by design. We currently index 12 sourcing countries (China, Vietnam, Bangladesh, India, Turkey, Portugal, Italy, Pakistan, Indonesia, Mexico, Cambodia, Morocco) and onboard new markets quarterly. Boutique factories from any country are welcome to apply.',
  },
  {
    q: 'What are the MOQ requirements to be listed?',
    a: 'There is no fixed MOQ requirement. Giwahs intentionally indexes factories from boutique (MOQ 100) to high-volume (MOQ 50,000+) to give buyers a full spectrum. Be honest about your real MOQ ranges — buyers self-filter against them.',
  },
  {
    q: 'How is the trust score calculated?',
    a: 'Trust score is computed from six signals: business registration status, verified certifications, response performance, verified factory media uploads, buyer reviews, and verification history. It is recomputed weekly. Featured placement and tier subscriptions do not influence trust score in any way.',
  },
  {
    q: 'Do you charge commission on orders?',
    a: 'No. Giwahs is an intelligence platform, not a marketplace. We do not process transactions, hold escrow, or take commission. Featured placements are a flat monthly subscription. Buyer ↔ supplier negotiation happens directly between parties.',
  },
  {
    q: 'What happens after I apply?',
    a: 'Our supplier intelligence team reviews submissions within 5 business days. If accepted, we onboard you onto the directory (free baseline listing), assess your verification tier, and walk you through Featured tier options if you\'re interested in promotional placement.',
  },
  {
    q: 'Are the tier metrics shown on this page real?',
    a: 'The performance ranges shown on the tier cards are illustrative reference figures based on category benchmarks. Real, live performance numbers are surfaced in your supplier dashboard once you\'re onboarded — including impressions, profile views, RFQ count, and save count, updated daily.',
  },
];

const FAQItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-neutral-200" data-testid={`faq-item-${idx}`}>
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-start justify-between gap-4 py-5 text-left"
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-display text-lg leading-tight tracking-tight md:text-xl" style={{ fontWeight: 600 }}>{q}</span>
        {open ? <Minus size={18} strokeWidth={1.5} className="mt-1 shrink-0" /> : <Plus size={18} strokeWidth={1.5} className="mt-1 shrink-0" />}
      </button>
      {open && <p className="pb-6 text-sm leading-relaxed text-neutral-700">{a}</p>}
    </div>
  );
};

const ForSuppliers = () => {

  const [form, setForm] = useState({
    company: '', country: '', categories: [], website: '', contactName: '',
    email: '', whatsapp: '', capacity: '', exportMarkets: '', interest: 'spotlight',
  });
  const { mutate: submitApplication, loading: submitting, error: submitError } = useSubmitSupplierApplication();
  const [formStarted, setFormStarted] = useState(false);

  // Form-start fires once when the supplier begins typing in any field.
  const markStarted = () => {
    if (formStarted) return;
    setFormStarted(true);
    track(EVENTS.SUPPLIER_APP_STARTED, {});
  };

  const toggleCategory = (slug) => {
    setForm(prev => ({
      ...prev,
      categories: prev.categories.includes(slug)
        ? prev.categories.filter(c => c !== slug)
        : [...prev.categories, slug],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.country || !form.contactName || !form.email) {
      toast.error('Please complete company, country, contact name, and email.');
      return;
    }
    try {
      await submitApplication({
        factory_name: form.company,
        contact_name: form.contactName,
        contact_email: form.email,
        contact_phone: form.whatsapp,
        country: form.country,
        categories: form.categories,
        monthly_capacity: form.capacity,
        website_url: form.website,
        selected_tier: form.interest,
        notes: form.exportMarkets ? `Export markets: ${form.exportMarkets}` : null,
      });
      track(EVENTS.SUPPLIER_APP_SUBMITTED, {
        tier: form.interest,
        country: form.country,
        category_count: form.categories.length,
        has_website: !!form.website,
      });
      toast.success('Application received — the Giwahs supplier team will review within 5 business days.');
      setForm({ company: '', country: '', categories: [], website: '', contactName: '', email: '', whatsapp: '', capacity: '', exportMarkets: '', interest: 'spotlight' });
    } catch {
      toast.error('Could not submit your application. Please try again.');
    }
  };

  const ic = "w-full border border-black bg-white px-3 py-3 text-sm placeholder:text-neutral-400 focus:outline-none";

  return (
    <div data-testid="for-suppliers-page" className="surface-obsidian text-platinum">
      <SEO
        title="For Suppliers — Get Featured on GIWAHS"
        description="Apply to join GIWAHS — the editorial intelligence platform connecting verified factories with global buyers. Free baseline listing. Featured placement tiers from $180/month."
        keywords="get featured factory, supplier acquisition, factory directory listing"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map(f => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }}
      />
      {/* 1. HERO */}
      <section className="relative overflow-hidden border-b border-black bg-[#0A0A0A] text-white">
        <div className="absolute inset-0">
          <Img src={HERO_BG} alt="" variant="hero" priority className="h-full w-full object-cover opacity-50" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative mx-auto max-w-[1440px] px-5 py-20 md:px-8 md:py-32">
          <div className="flex items-center gap-3 label-overline text-neutral-300">
            <span className="h-px w-10 bg-white" />
            For Factories · Manufacturers · Suppliers
          </div>
          <h1 className="mt-7 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 8vw, 7rem)', lineHeight: 0.9 }}>
            GET DISCOVERED<br />BY GLOBAL BUYERS.
          </h1>
          <p className="mt-7 max-w-2xl text-base leading-relaxed text-neutral-300 md:text-lg">
            Join the editorial intelligence layer used by retailers, brands, and entrepreneurs to find verified factories worldwide. Free baseline listing — premium placement available.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#apply" data-testid="hero-apply-cta" className="btn-giwahs btn-giwahs-dark inline-flex items-center gap-2">
              Apply to Join <ArrowUpRight size={14} strokeWidth={1.5} />
            </a>
            <a href="#tiers" className="btn-giwahs border border-white/30 bg-transparent text-white hover:bg-white hover:text-black inline-flex items-center gap-2">
              See placement tiers <ArrowDown size={14} strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>

      {/* 2. WHY SUPPLIERS JOIN */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <span className="label-overline">/ 01 — Why Join</span>
              <h2 className="mt-5 font-display text-4xl leading-tight tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>
                Why factories choose Giwahs.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-neutral-700">
                We're not a transactional marketplace. We're a sourcing intelligence platform — and the buyers we attract are decision-makers, not bargain hunters. They come to Giwahs to find serious partners.
              </p>
            </div>
            <div className="grid gap-px bg-black md:col-span-7 sm:grid-cols-2">
              {BENEFITS.map((b, i) => (
                <div key={i} className="bg-white p-6" data-testid={`benefit-${i}`}>
                  <b.icon size={20} strokeWidth={1.5} />
                  <div className="mt-4 font-display text-lg tracking-tight md:text-xl" style={{ fontWeight: 600 }}>{b.title}</div>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">{b.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 3. FEATURED PLACEMENT TIERS */}
      <section id="tiers" className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <span className="label-overline">/ 02 — Featured Placement Tiers</span>
              <h2 className="mt-4 font-display text-4xl leading-tight tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>
                Choose your visibility.
              </h2>
              <p className="mt-4 max-w-xl text-neutral-700">
                Featured placements are <strong className="text-black">sponsored, clearly labelled, and entirely separate from your trust score and verification badges.</strong> Baseline directory listing is free — tiers below are optional promotional placement.
              </p>
            </div>
            <span className="font-mono-data text-xs text-neutral-500">MOCK PRICING · 2026 PREVIEW</span>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {TIERS.map((t, i) => (
              <div
                key={t.key}
                data-testid={`tier-card-${t.key}`}
                className={`flex flex-col p-7 ${t.accent} ${t.key === 'boost' ? 'md:-mt-4 md:mb-0 md:shadow-[8px_8px_0_0_#0A0A0A]' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`label-overline ${t.key === 'spotlight' ? 'text-neutral-400' : 'text-neutral-500'}`}>{t.badge}</span>
                  <span className={`font-mono-data text-[10px] ${t.key === 'spotlight' ? 'text-neutral-500' : 'text-neutral-400'}`}>TIER {String(i + 1).padStart(2, '0')}</span>
                </div>
                <div className="mt-6 font-display text-4xl tracking-tighter md:text-5xl" style={{ fontWeight: 700 }}>
                  {t.label}
                </div>
                <div className="mt-5 flex items-baseline gap-1.5">
                  <span className="font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 700 }}>${t.price}</span>
                  <span className={`font-mono-data text-xs ${t.key === 'spotlight' ? 'text-neutral-400' : 'text-neutral-500'}`}>/ month</span>
                </div>
                <div className={`mt-6 grid grid-cols-3 gap-3 border-y py-4 ${t.key === 'spotlight' ? 'border-white/20' : 'border-neutral-300'}`}>
                  <div>
                    <div className={`label-overline ${t.key === 'spotlight' ? 'text-neutral-500' : 'text-neutral-500'}`}>Impressions</div>
                    <div className="mt-1 font-mono-data text-xs">{t.impressions}</div>
                  </div>
                  <div>
                    <div className={`label-overline ${t.key === 'spotlight' ? 'text-neutral-500' : 'text-neutral-500'}`}>RFQs</div>
                    <div className="mt-1 font-mono-data text-xs">{t.rfqs}</div>
                  </div>
                  <div>
                    <div className={`label-overline ${t.key === 'spotlight' ? 'text-neutral-500' : 'text-neutral-500'}`}>Saves</div>
                    <div className="mt-1 font-mono-data text-xs">{t.saves}</div>
                  </div>
                </div>
                <ul className="mt-5 space-y-2.5 text-sm leading-relaxed">
                  {t.placements.map((p, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <Check size={14} strokeWidth={1.6} className="mt-0.5 shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <p className={`mt-6 text-xs leading-relaxed ${t.key === 'spotlight' ? 'text-neutral-400' : 'text-neutral-600'}`}>
                  {t.notes}
                </p>
                <a
                  href="#apply"
                  data-testid={`tier-cta-${t.key}`}
                  className={`btn-giwahs mt-7 inline-flex items-center justify-center gap-2 ${t.key === 'spotlight' ? 'btn-giwahs-dark' : 'btn-giwahs-primary'}`}
                >
                  Apply for {t.label} <ArrowUpRight size={14} strokeWidth={1.5} />
                </a>
              </div>
            ))}
          </div>

          <div className="mt-8 border border-black bg-white p-5 md:p-6">
            <div className="flex items-start gap-3">
              <Megaphone size={18} strokeWidth={1.5} className="mt-0.5 shrink-0" />
              <p className="text-sm leading-relaxed text-neutral-700">
                <strong className="text-black">Important — credibility separation:</strong> Featured placement is a paid promotional slot. It does <strong>not</strong> grant verification badges, raise your trust score, or change your position in the regular trust-sorted directory grid. Verification and trust are assessed independently by the Giwahs intelligence team.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. VERIFICATION SYSTEM */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 03 — Verification System</span>
          <h2 className="mt-4 font-display text-4xl leading-tight tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>
            How buyers know you're real.
          </h2>
          <p className="mt-4 max-w-2xl text-neutral-700">
            Four-tier verification grounded in documentation. <strong className="text-black">Independent of paid placement.</strong>
          </p>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <VerificationBadgeFull type="business" />
            <VerificationBadgeFull type="video" />
            <VerificationBadgeFull type="audit" />
            <VerificationBadgeFull type="premium" />
          </div>
          <div className="mt-8 grid gap-3 md:grid-cols-2">
            <div className="border border-neutral-300 p-5">
              <div className="label-overline">Trust Score</div>
              <p className="mt-3 text-sm leading-relaxed text-neutral-700">
                Computed weekly from six signals: business registration, certifications, response performance, factory media uploads, buyer reviews, and verification history. Featured placement has zero impact on trust score.
              </p>
            </div>
            <div className="border border-neutral-300 p-5">
              <div className="label-overline">Editorial review</div>
              <p className="mt-3 text-sm leading-relaxed text-neutral-700">
                Our team reviews documentation submitted with every verification badge. Buyers see the source of each verification claim in your profile intelligence brief.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. SUPPLIER SUCCESS METRICS */}
      <section className="border-b border-black bg-[#0A0A0A] text-white grain-overlay">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <div className="flex items-end justify-between">
            <div>
              <span className="label-overline text-neutral-400">/ 04 — Platform Reach</span>
              <h2 className="mt-4 font-display text-4xl leading-tight tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>
                What we move every month.
              </h2>
            </div>
            <span className="font-mono-data text-[11px] text-neutral-500">ILLUSTRATIVE · 2026 PROJECTION</span>
          </div>
          <div className="mt-12 grid gap-px bg-neutral-800 sm:grid-cols-2 lg:grid-cols-4">
            {METRICS.map((m, i) => (
              <div key={i} className="bg-[#0A0A0A] p-7" data-testid={`metric-${i}`}>
                <FileBarChart size={16} strokeWidth={1.5} className="text-neutral-500" />
                <div className="mt-6 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.25rem, 4vw, 3.75rem)', lineHeight: 0.95 }}>{m.value}</div>
                <div className="mt-3 label-overline text-neutral-400">{m.label}</div>
                <p className="mt-2 text-xs leading-relaxed text-neutral-500">{m.sub}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 grid gap-px bg-neutral-800 md:grid-cols-3">
            {[
              { i: Eye, t: 'Profile Views', d: 'Real-time analytics in your supplier dashboard.' },
              { i: Bookmark, t: 'Buyer Saves', d: 'See which buyers have shortlisted your profile.' },
              { i: Inbox, t: 'RFQ Inbox', d: 'Structured quote requests with full project briefs.' },
            ].map((b, i) => (
              <div key={i} className="bg-[#0A0A0A] p-6">
                <b.i size={18} strokeWidth={1.5} />
                <div className="mt-4 font-display text-lg tracking-tight" style={{ fontWeight: 600 }}>{b.t}</div>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] grid gap-12 px-5 py-16 md:grid-cols-12 md:px-8 md:py-24">
          <div className="md:col-span-4">
            <span className="label-overline">/ 05 — FAQ</span>
            <h2 className="mt-4 font-display text-4xl leading-tight tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>
              Common questions.
            </h2>
            <p className="mt-5 text-neutral-700">If something isn't covered here, reach out — we respond within one business day.</p>
            <Link to="/contact" className="mt-5 inline-flex items-center gap-1 border-b border-black pb-0.5 label-overline">
              Contact us <ArrowUpRight size={12} strokeWidth={1.5} />
            </Link>
          </div>
          <div className="md:col-span-8" data-testid="faq-section">
            <div className="border-t border-neutral-200">
              {FAQS.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} idx={i} />)}
            </div>
          </div>
        </div>
      </section>

      {/* 7. LEAD CAPTURE FORM */}
      <section id="apply" className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-5">
              <span className="label-overline">/ 06 — Apply to Join</span>
              <h2 className="mt-4 font-display text-4xl leading-tight tracking-tighter md:text-5xl" style={{ fontWeight: 600 }}>
                Apply to become a featured supplier.
              </h2>
              <p className="mt-5 text-neutral-700">
                Our supplier intelligence team reviews submissions within 5 business days. There is no fee to apply or to be listed on the directory — Featured placement is optional.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  ['01', 'Submit application', 'Tell us about your factory and capabilities.'],
                  ['02', 'Verification review', 'Our team reviews docs and assesses badge eligibility.'],
                  ['03', 'Onboard to directory', 'Free baseline listing goes live within 7 days.'],
                  ['04', 'Choose Featured tier', 'Optional — Spotlight / Boost / Standard.'],
                ].map(([n, t, d]) => (
                  <div key={n} className="flex gap-4 border-l-2 border-black pl-4">
                    <span className="font-mono-data text-sm text-neutral-500">{n}</span>
                    <div>
                      <div className="font-display text-base tracking-tight" style={{ fontWeight: 600 }}>{t}</div>
                      <p className="text-sm text-neutral-600">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <form onSubmit={submit} onFocus={markStarted} data-testid="supplier-application-form" className="border border-black bg-white p-6 md:col-span-7 md:p-10">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label-overline">Company Name *</label>
                  <input data-testid="apply-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={`${ic} mt-2`} placeholder="Northgate Apparel Collective" />
                </div>
                <div>
                  <label className="label-overline">Country *</label>
                  <select data-testid="apply-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={`${ic} mt-2`}>
                    <option value="">Select country</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label className="label-overline">Product Categories</label>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {PRODUCT_CATEGORIES.map(c => {
                    const active = form.categories.includes(c.slug);
                    return (
                      <button
                        type="button"
                        key={c.slug}
                        onClick={() => toggleCategory(c.slug)}
                        data-testid={`apply-cat-${c.slug}`}
                        className={`label-overline rounded-full border px-4 py-1.5 transition-colors ${active ? 'border-black bg-black text-white' : 'border-neutral-300 bg-white text-neutral-700 hover:border-black hover:text-black'}`}
                      >
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label-overline">Website</label>
                  <input data-testid="apply-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={`${ic} mt-2`} placeholder="https://" />
                </div>
                <div>
                  <label className="label-overline">Contact Name *</label>
                  <input data-testid="apply-contact-name" value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} className={`${ic} mt-2`} />
                </div>
                <div>
                  <label className="label-overline">Email *</label>
                  <input data-testid="apply-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${ic} mt-2`} />
                </div>
                <div>
                  <label className="label-overline">WhatsApp</label>
                  <input data-testid="apply-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className={`${ic} mt-2`} placeholder="+xx ..." />
                </div>
                <div>
                  <label className="label-overline">Monthly Production Capacity</label>
                  <input data-testid="apply-capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className={`${ic} mt-2`} placeholder="e.g. 120,000 units" />
                </div>
                <div>
                  <label className="label-overline">Export Markets</label>
                  <input data-testid="apply-export-markets" value={form.exportMarkets} onChange={(e) => setForm({ ...form, exportMarkets: e.target.value })} className={`${ic} mt-2`} placeholder="EU, USA, Japan" />
                </div>
              </div>

              <div className="mt-5">
                <label className="label-overline">Interest Level — Featured Tier</label>
                <div className="mt-2 grid grid-cols-1 gap-0 border border-black sm:grid-cols-4">
                  {[
                    ['none', 'Listing only', 'Free'],
                    ['standard', 'Standard', '$180/mo'],
                    ['boost', 'Boost', '$480/mo'],
                    ['spotlight', 'Spotlight', '$1,200/mo'],
                  ].map(([k, l, p], i, arr) => (
                    <label
                      key={k}
                      data-testid={`apply-interest-${k}`}
                      className={`flex cursor-pointer flex-col items-start p-3 transition-colors ${i < arr.length - 1 ? 'sm:border-r border-b sm:border-b-0 border-black' : ''} ${form.interest === k ? 'bg-black text-white' : 'bg-white text-black hover:bg-neutral-100'}`}
                    >
                      <input type="radio" name="interest" value={k} checked={form.interest === k} onChange={(e) => setForm({ ...form, interest: e.target.value })} className="hidden" />
                      <span className="label-overline">{l}</span>
                      <span className={`mt-1 font-mono-data text-xs ${form.interest === k ? 'text-neutral-300' : 'text-neutral-500'}`}>{p}</span>
                    </label>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                data-testid="apply-submit"
                disabled={submitting}
                className="btn-giwahs btn-giwahs-primary mt-7 inline-flex w-full items-center justify-center gap-2 sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? <><Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> Submitting…</> : <>Submit Application <ArrowUpRight size={14} strokeWidth={1.5} /></>}
              </button>
              {submitError && (
                <p className="mt-3 text-xs text-red-700" data-testid="apply-error">Failed to submit. Please try again.</p>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* 8. FINAL CTA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1440px] px-5 py-20 md:px-8 md:py-32">
          <div className="grid items-end gap-10 md:grid-cols-2">
            <h2 className="font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 7vw, 6.5rem)', lineHeight: 0.92 }}>
              Apply to become a featured supplier.
            </h2>
            <div className="flex flex-col gap-5 md:items-end">
              <p className="max-w-md text-neutral-400">
                Free to list. Optional Featured tiers from $180/month. Application review within 5 business days.
              </p>
              <a href="#apply" data-testid="final-cta" className="btn-giwahs btn-giwahs-dark inline-flex w-fit items-center gap-2">
                Apply to Become a Featured Supplier <ArrowUpRight size={14} strokeWidth={1.5} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForSuppliers;
